import React from 'react';
import { CardNumberElement, Elements, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import cx from 'classnames';
import { Formik, FormikHelpers } from 'formik';

import { DangerAlert } from '@magoosh/lib/alerts';
import { PrimaryButton, SecondaryButton } from '@magoosh/lib/button';
import { Form } from '@magoosh/lib/forms';
import useAuthenticityToken from '@magoosh/utils/use_authenticity_token';
import { bugsnagNotify } from 'utilities/bugsnag';
import { formatPrice } from 'utilities/money';

import * as api from '@payments/api';
import StripeCardInputs from '@payments/components/inputs/stripe_card';
import { genericErrorMessage, PaymentError } from '@payments/errors';
import * as utils from '@payments/utils';

import style from '@payments/style.module.scss';

interface PayRemainingBalanceProps {
  paymentDetails: {
    cardBrand: string;
    cardLast4: string;
    email: string;
    paymentMethod: string;
    postalCode: string;
    stripePaymentMethodId: string;
  };
  price: number;
  stripeToken: string;
  subscriptionId: number;
  subscriptionName: string;
}

const PayRemainingBalance: React.FC<PayRemainingBalanceProps> = (props) => {
  const authenticityToken = useAuthenticityToken();

  const elements = useElements();
  const stripe = useStripe();

  const ERROR = 'error';
  const SUCCESS = 'success';

  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [useSavedMethod, setUseSavedMethod] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState(null);

  const handleConfirmation = async (formValues: null | CardFormValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    let values = {
      authenticityToken,
      stripePaymentMethodId: props.paymentDetails.stripePaymentMethodId
    };

    if (formValues) values = { ...values, ...formValues };

    try {
      const response = await api.payRemainingBalance(stripe, values, props.subscriptionId);

      if (response.nextStepUrl) {
        // Give Stripe a couple seconds to finish processing
        setTimeout(() => {
          window.location.assign(response.nextStepUrl);
        }, 2000);
      } else {
        throw new PaymentError(
          genericErrorMessage,
          `Unexpectedly reached the end of handleConfirmation in PayRemainingBalance with values ${JSON.stringify(values)} and response ${JSON.stringify(response)}`
        );
      }
    } catch(exception) {
      setErrorMessage(genericErrorMessage);
      bugsnagNotify(exception.debug);
      setIsSubmitting(false);
    }
  };

  // Create the payment method in Stripe, then pass it to handleConfirmation to submit to Magoosh.
  const onSubmitCard = async (values: CardFormValues, { setErrors }) => {
    setIsSubmitting(true);
    setErrorMessage(null);

    const card = elements.getElement(CardNumberElement);

    try {
      const response = await api.createStripePaymentMethod(stripe, card, values as any);

      return handleConfirmation({
        ...values,
        stripePaymentMethodId: response.stripePaymentMethod.id
      });
    } catch(exception) {
      setErrors(exception.errors);
      bugsnagNotify(exception.debug);
      setIsSubmitting(false);
    }
  };

  const renderUseSavedMethod = () => {
    return (
      <div>
        <p className="text-center h4">
          Use the {props.paymentDetails.cardBrand.toLocaleUpperCase()} credit card ending in{' '}
          <b>{props.paymentDetails.cardLast4}</b> to pay the remaining balance?
        </p>

        <div className="d-flex u-margin-V-l justify-content-center">
          <PrimaryButton onClick={handleConfirmation} submitting={isSubmitting} >
            Yes, pay {formatPrice(props.price)}
          </PrimaryButton>

          <SecondaryButton
            className="u-margin-L-s"
            disabled={isSubmitting}
            onClick={() => setUseSavedMethod(false)}>
            No, use a different card
          </SecondaryButton>
        </div>
      </div>
    );
  };

  interface CardFormValues {
    cardName: string;
    cardZipCode: string;
    stripePaymentMethodId: any;
  }

  const initialValues = {
    cardName: '',
    cardZipCode: '',
    stripePaymentMethodId: ''
  };

  const renderCreditCardFields = () => {
    return (
      <div>
        <div className="d-flex u-margin-V-l justify-content-center">
          <SecondaryButton disabled={isSubmitting} onClick={() => setUseSavedMethod(true)}>
            Actually, use my saved method
          </SecondaryButton>
        </div>
        <Formik initialValues={initialValues} onSubmit={onSubmitCard}>
          {({ values, errors, handleSubmit }) => {
            return (
              <Form id="subscription_payment_fields">
                <StripeCardInputs />

                <div className="d-flex u-margin-V-l justify-content-center">
                  <PrimaryButton
                    className={cx(style.btnBlockOnMobile, 'u-padding-H-xl')}
                    onClick={handleSubmit}
                    submitting={isSubmitting}
                    type="submit"
                  >
                    Submit Payment
                  </PrimaryButton>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
    );
  };

  return (
    <div className="u-margin-T-m">
      {errorMessage && <DangerAlert className="form-errors small">{errorMessage}</DangerAlert>}

      <div className="u-margin-T-m">
        <p className="text-center h4">
          Your installment plan has a remaining balance of <b>{formatPrice(props.price)}</b>.
        </p>

        {useSavedMethod ? renderUseSavedMethod() : renderCreditCardFields()}
      </div>
    </div>
  );
};

const Wrapper: React.FC<PayRemainingBalanceProps> = (props) => {
  const stripePromise = loadStripe(props.stripeToken);
  return <Elements stripe={stripePromise}>{props.children}</Elements>;
};

const WrappedPayRemainingBalance: React.FC<PayRemainingBalanceProps> = (props) => {
  return (
    <Wrapper {...props}>
      <PayRemainingBalance {...props} />
    </Wrapper>
  );
};

export default WrappedPayRemainingBalance;
